import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useMobile } from "@/hooks/use-mobile";
import type { TopupCounts } from "@/lib/topup";
import type { RouteTicketData } from "@/types/topup/routeticket";
import { Minus, Plus, Users, X } from "lucide-react";
import { Fragment, useState } from "react";
import { cn } from "@/lib/utils";

const FIELD_CLASS =
  "flex h-12 w-full items-center justify-between rounded-2xl border-0 bg-primary-soft px-4 py-3.5 text-sm font-semibold text-foreground hover:bg-primary-soft";
const MAX_PAX_PER_TYPE = 100;

type PaxRow = {
  key: string;
  label: string;
  ageFrom: number;
  ageTo: number;
  category: string;
};

type PaxGroup = {
  category: string;
  rows: PaxRow[];
};

// Malaysian/Foreign are shown first with their label; any other category
// (e.g. local) is appended last without a visible label.
const CATEGORY_ORDER = ["Malaysian", "Foreign"];

const createPaxRows = (ticketTypes: RouteTicketData[]): PaxRow[] =>
  ticketTypes.map((ticket) => ({
    key: ticket.mdTicketTypeId,
    label: ticket.ticketTypeName,
    ageFrom: ticket.ageFrom,
    ageTo: ticket.ageTo,
    category: ticket.ticketTypeCategoryName,
  }));

const groupPaxRows = (rows: PaxRow[]): PaxGroup[] => {
  const groups: PaxGroup[] = [];

  rows.forEach((row) => {
    const group = groups.find((item) => item.category === row.category);

    if (group) {
      group.rows.push(row);
    } else {
      groups.push({ category: row.category, rows: [row] });
    }
  });

  return groups.sort((a, b) => {
    const rank = (category: string) => {
      const index = CATEGORY_ORDER.indexOf(category);
      return index === -1 ? CATEGORY_ORDER.length : index;
    };

    return rank(a.category) - rank(b.category);
  });
};

type PaxSelectProps = {
  label?: string;
  value: TopupCounts;
  ticketTypes: RouteTicketData[];
  maxTotal?: number;
  onChange: (value: TopupCounts) => void;
};

type StepperRowProps = {
  label: string;
  ageFrom: number;
  ageTo: number;
  value: number;
  min: number;
  max: number;
  disableIncrement: boolean;
  onChange: (value: number) => void;
  onDecrement: () => void;
  onIncrement: () => void;
};

function StepperRow({
  label,
  ageFrom,
  ageTo,
  value,
  min,
  max,
  disableIncrement,
  onChange,
  onDecrement,
  onIncrement,
}: StepperRowProps) {
  const [isInputEmpty, setIsInputEmpty] = useState(false);

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col">
        <span className="text-sm font-medium text-muted-foreground">
          {label}
        </span>
        {(ageFrom !== 0 || ageTo !== 0) && (
          <span className="text-xs text-muted">
            Age {ageTo >= 99 ? `${ageFrom}+` : `${ageFrom}-${ageTo}`}
          </span>
        )}
      </div>
      
      <div className="flex items-center gap-4">
        <Button
          type="button"
          variant="outline"
          className="h-10 w-10 aspect-square border-primary bg-card rounded-full p-0"
          onClick={onDecrement}
          disabled={value <= min}
          aria-label={`Decrease ${label}`}
        >
          <Minus size={18} className="text-primary" aria-hidden="true" />
        </Button>
        <input
          type="text"
          min={min}
          max={max}
          value={isInputEmpty && value === min ? "" : value}
          onChange={(event) => {
            const nextValue = event.target.value;

            if (nextValue === "") {
              setIsInputEmpty(true);
              onChange(min);
              return;
            }

            const next = Number(nextValue);

            if (Number.isNaN(next)) return;

            const clamped = Math.min(max, Math.max(min, next));
            setIsInputEmpty(false);
            onChange(clamped);
          }}
          onBlur={() => {
            setIsInputEmpty(false);
          }}
          className="w-12 appearance-none bg-transparent text-center text-base font-bold text-foreground outline-none"
          aria-label={`${label} count`}
        />
        <Button
          type="button"
          variant="outline"
          className="h-10 w-10 aspect-square border-primary bg-card rounded-full p-0"
          onClick={onIncrement}
          disabled={disableIncrement}
          aria-label={`Increase ${label}`}
        >
          <Plus size={18} className="text-primary" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

export function PaxSelect({
  label = "Passengers",
  value,
  ticketTypes,
  maxTotal,
  onChange,
}: PaxSelectProps) {
  const isMobile = useMobile();
  const [open, setOpen] = useState(false);
  const paxRows = createPaxRows(ticketTypes);
  const paxGroups = groupPaxRows(paxRows);
  const [draft, setDraft] = useState<TopupCounts>(value);

  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(value);
    setOpen(next);
  };

  const draftTotal = paxRows.reduce(
    (sum, row) => sum + (draft[row.key] ?? 0),
    0,
  );
  const isFull = maxTotal !== undefined && draftTotal >= maxTotal;

  const update = (key: string, next: number) =>
    setDraft((prev) => ({
      ...prev,
      [key]: Math.min(MAX_PAX_PER_TYPE, Math.max(0, next)),
    }));

  const handleDone = () => {
    onChange(draft);
    setOpen(false);
  };

  const hasSelection = paxRows.some((row) => (value[row.key] ?? 0) > 0);

  const summary =
    paxRows
      .filter((row) => (value[row.key] ?? 0) > 0)
      .map((row) => `${value[row.key]} ${row.label}`)
      .join(", ") || "Select passengers";

  const trigger = (
    <Button type="button" className={FIELD_CLASS}>
      <span
        className={cn(
          "truncate text-sm font-medium",
          hasSelection ? "text-foreground" : "text-border",
        )}
      >
        {summary}
      </span>
      <Users size={16} aria-hidden="true" />
    </Button>
  );

const stepper = (
  <div className="flex flex-col gap-4">
    {/* Scrollable passenger list */}
    <div className="max-h-[50vh] overflow-y-auto pr-2">
      <div className="flex flex-col gap-3">
        {paxGroups.map((group, groupIndex) => (
          <Fragment key={group.category}>
            {groupIndex > 0 && (
              <Separator className="border-border" />
            )}

            <span className="text-xs font-bold uppercase text-primary">
              {group.category}
            </span>

            {group.rows.map((row, rowIndex) => (
              <Fragment key={row.key}>
                {rowIndex > 0 && (
                  <Separator className="border-border" />
                )}

                <StepperRow
                  label={row.label}
                  ageFrom={row.ageFrom}
                  ageTo={row.ageTo}
                  value={draft[row.key] ?? 0}
                  min={0}
                  max={MAX_PAX_PER_TYPE}
                  disableIncrement={
                    isFull ||
                    (draft[row.key] ?? 0) >= MAX_PAX_PER_TYPE
                  }
                  onChange={(next) => update(row.key, next)}
                  onDecrement={() =>
                    update(row.key, (draft[row.key] ?? 0) - 1)
                  }
                  onIncrement={() =>
                    update(row.key, (draft[row.key] ?? 0) + 1)
                  }
                />
              </Fragment>
            ))}
          </Fragment>
        ))}
      </div>
    </div>

    {/* Fixed button */}
    <Button
      type="button"
      variant="default"
      className="w-full"
      onClick={handleDone}
    >
      Done
    </Button>
  </div>
);


  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold text-field">{label}</Label>
      {isMobile ? (
        <Drawer open={open} onOpenChange={handleOpenChange} showSwipeHandle>
          <DrawerTrigger render={trigger} />
          <DrawerContent>
            <DrawerClose className="p-4 border-b border-border">
              <Button
                variant="outline"
                className="z-50 rounded-full h-10 w-10"
                onClick={() => setOpen(false)}
              >
                <X size={24} className="text-muted shrink-0" />
              </Button>
            </DrawerClose>
            <div className="p-4">{stepper}</div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger render={trigger} />
          <PopoverContent
            align="start"
            className="w-(--anchor-width) rounded-2xl p-4 shadow-xl"
          >
            {stepper}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

